/**
 * The Creator's step sequence, per entry mode.
 *
 * The classic entry keeps its three steps untouched. The guided entry adds a
 * catalogue between design and products: the seller picks products from the
 * whole range there, and the products step then shows only what was picked
 * (see docs/design/justdroppit-flow-v3 — 01 Design, 02 Katalog, 03 Produkte,
 * 04 Erstellen). Everything that renders, numbers or advances steps reads the
 * sequence from here rather than hard-coding it.
 */

export type CreatorStep = 'design' | 'catalog' | 'products' | 'publish';

/**
 * How the Creator was entered.
 * - `classic`: the canvas editor on step 1, as before.
 * - `guided`: motif-first surface on step 1, catalogue on step 2.
 */
export type CreatorEntryMode = 'classic' | 'guided';

export const CLASSIC_STEPS: readonly CreatorStep[] = [
  'design',
  'products',
  'publish',
];

export const GUIDED_STEPS: readonly CreatorStep[] = [
  'design',
  'catalog',
  'products',
  'publish',
];

const ALL_STEPS: readonly CreatorStep[] = GUIDED_STEPS;

export function stepsForEntryMode(
  mode: CreatorEntryMode
): readonly CreatorStep[] {
  return mode === 'guided' ? GUIDED_STEPS : CLASSIC_STEPS;
}

export function isKnownStep(value: string): value is CreatorStep {
  return (ALL_STEPS as readonly string[]).includes(value);
}

/** The step after `current` in `steps`; null on the last step or an unknown one. */
export function nextStep(
  steps: readonly CreatorStep[],
  current: CreatorStep
): CreatorStep | null {
  const index = steps.indexOf(current);
  if (index === -1 || index === steps.length - 1) return null;
  return steps[index + 1] ?? null;
}

export function isLastStep(
  steps: readonly CreatorStep[],
  current: CreatorStep
): boolean {
  return steps.length > 0 && steps[steps.length - 1] === current;
}

/** "01", "02", … as shown on the step pills. */
export function stepNumber(
  steps: readonly CreatorStep[],
  step: CreatorStep
): string {
  return String(steps.indexOf(step) + 1).padStart(2, '0');
}

/**
 * A step that does not exist in this mode maps to its nearest equivalent:
 * the catalogue is the classic products step. Anything else passes through.
 */
export function normalizeStepForMode(
  step: CreatorStep,
  mode: CreatorEntryMode
): CreatorStep {
  if (stepsForEntryMode(mode).includes(step)) return step;
  if (step === 'catalog') return 'products';
  return 'design';
}

/** True when `target` comes after `from` in the sequence. */
export function isForward(
  steps: readonly CreatorStep[],
  from: CreatorStep,
  target: CreatorStep
): boolean {
  return steps.indexOf(target) > steps.indexOf(from);
}
